import { useEffect, useRef, useState } from 'react';
import { toLonLat } from 'ol/proj';
import { unByKey } from 'ol/Observable';
import TileLayer from 'ol/layer/Tile';
import type TileSource from 'ol/source/Tile';
import { useMapContext } from './MapContext';
import { useNavigation } from '../navigation/NavigationContext';
import { GEOSERVER_HOST, WMS_URL } from '../config/env';
import { useWorkspaceBySlug } from '../api/WorkspacesContext';
import { styleForWorkspace } from '../pages/HomePage/workspaceStyle';
import { BottomToolbar } from '../components/BottomToolbar/BottomToolbar';
import { IdentifyPopup } from '../components/IdentifyPopup/IdentifyPopup';
import { CompareSwipe } from '../components/CompareSwipe/CompareSwipe';
import { MeasurementDetailPanel } from '../components/MeasurementDetailPanel/MeasurementDetailPanel';

export function MapView() {
  const { map, layers, geoserverUnreachable, measurements, selectedMeasurementId, selectMeasurement } =
    useMapContext();
  const selectedMeasurement = measurements.find((m) => m.id === selectedMeasurementId) ?? null;
  const { focusGroupId } = useNavigation();
  // Logistics / Crop & Field Growth / Water Borehole / Field Operations: real, navigable
  // workspaces with no underlying data anywhere in the system yet — an honest empty-state banner
  // over the (fully functional) basemap, not fabricated pins/polygons and not a broken-looking
  // stub page. Workspace data itself comes from the API (WorkspacesContext, ROADMAP.md v0.6.2);
  // `null` here just means "still loading" or "not a real workspace" — MapView already renders
  // fine either way (no banner shown) since the underlying map keeps working regardless.
  const currentWorkspace = useWorkspaceBySlug(focusGroupId);
  const emptyWorkspace = currentWorkspace?.empty ? currentWorkspace : null;
  const EmptyWorkspaceIcon = styleForWorkspace(emptyWorkspace?.icon ?? null).Icon;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [coord, setCoord] = useState<[number, number] | null>(null);
  const [tilesFailing, setTilesFailing] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    map.setTarget(containerRef.current);
    return () => map.setTarget(undefined);
  }, [map]);

  // Live reachability of GeoServer's tile sources. A single 'tileloaderror' proves nothing — a
  // tile request aborted while zooming/panning, one slow render timing out, or one bad layer all
  // fire it while GeoServer itself is perfectly healthy, and the old behaviour (flag on the first
  // error, clear only when some later tile happened to load) left a false "GeoServer isn't
  // responding" banner stuck over a fully rendered map. So an error only schedules a confirmation:
  // after a short settle delay, ask GeoServer itself (a WMS GetCapabilities — public, CORS-open,
  // the same service the tiles come from). Only if THAT fails is the banner shown; a later
  // successful tile or a successful probe clears it. Hidden layers are never requested (see
  // MapContext), so an error here is about a layer the user can actually see.
  // Deliberately excludes the OSM basemap so an OSM hiccup isn't reported as a GeoServer outage.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let lastProbe = 0;
    let disposed = false;
    const confirmDown = async () => {
      lastProbe = Date.now();
      let up = false;
      try {
        const res = await fetch(`${WMS_URL}?service=WMS&version=1.3.0&request=GetCapabilities`, {
          cache: 'no-store',
        });
        up = res.ok;
      } catch {
        up = false;
      }
      if (!disposed) setTilesFailing(!up);
    };
    const onError = () => {
      if (timer || Date.now() - lastProbe < 5000) return; // one probe per burst of errors
      timer = setTimeout(() => {
        timer = undefined;
        void confirmDown();
      }, 1500);
    };
    const onLoaded = () => setTilesFailing(false);
    const sources: TileSource[] = layers
      .filter((l) => l.sourceName && l.layer instanceof TileLayer)
      .map((l) => (l.layer as TileLayer).getSource())
      .filter((s): s is TileSource => !!s);
    sources.forEach((s) => {
      s.on('tileloaderror', onError);
      s.on('tileloadend', onLoaded);
    });
    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      sources.forEach((s) => {
        s.un('tileloaderror', onError);
        s.un('tileloadend', onLoaded);
      });
    };
  }, [layers]);

  useEffect(() => {
    const key = map.on('pointermove', (evt) => {
      setCoord(toLonLat(evt.coordinate) as [number, number]);
    });
    return () => unByKey(key);
  }, [map]);

  const geoserverDown = geoserverUnreachable || tilesFailing;

  return (
    <div className="map-view">
      <div ref={containerRef} className="ol-map-container" />
      {geoserverDown && (
        <div className="tile-warning" role="status">
          GeoServer ({GEOSERVER_HOST}) isn&rsquo;t responding — imagery layers are unavailable. The basemap and
          tools still work.
        </div>
      )}
      {emptyWorkspace && (
        <div className="workspace-empty-banner" role="status">
          <EmptyWorkspaceIcon size={22} />
          <div>
            <div className="workspace-empty-banner-title">{emptyWorkspace.title}</div>
            <p>{emptyWorkspace.empty_message}</p>
          </div>
        </div>
      )}
      <IdentifyPopup />
      <CompareSwipe />
      {selectedMeasurement && (
        // Keyed by id so switching the selection to a different measurement
        // mounts a fresh panel instance instead of reusing one whose local
        // state (draft description, in-progress rename) belongs to the
        // previous selection.
        <MeasurementDetailPanel
          key={selectedMeasurement.id}
          measurement={selectedMeasurement}
          onClose={() => selectMeasurement(null)}
        />
      )}
      <BottomToolbar />
      <div className="coord-readout">
        {coord ? `${coord[1].toFixed(6)}, ${coord[0].toFixed(6)}` : 'Move over the map'}
      </div>
    </div>
  );
}
